import { useParams } from 'react-router-dom'
import type { WorkflowRunNode, AgentMemoryEntry } from '@/types'
import { useNodeAgentMemory } from '@/hooks'
import { Badge, Button } from '@/components/ui'
import { cn } from '@/lib/utils'
import { getErrorMessage, getErrorStatus } from '@/api/errors'
import { Loader2, RefreshCw } from 'lucide-react'

interface Props {
  node: WorkflowRunNode
}

export function MemoryViewer({ node }: Props) {
  const { projectId, runId } = useParams()
  const { data, isLoading, isError, error, isFetching, refetch, isExtractionWindowOpen } = useNodeAgentMemory(
    projectId!,
    runId!,
    node.id,
    node.status === 'Completed' ? node.completedAt : null,
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-card" role="status" aria-live="polite" data-testid="node-memory-panel">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        <span className="sr-only">Loading what this node learned</span>
      </div>
    )
  }

  const entries = data?.entries ?? []

  return (
    <div className="p-4 overflow-y-auto h-full bg-card space-y-3" data-testid="node-memory-panel">
      <div className="flex items-start gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-foreground">Memory Persisted</h3>
          <p className="text-xs text-muted-foreground">After run – what it learned</p>
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="ml-auto h-7"
          disabled={isFetching}
          onClick={() => refetch()}
          aria-label="Refresh memory"
        >
          {isFetching
            ? <Loader2 className="h-3 w-3 animate-spin" />
            : <RefreshCw className="h-3 w-3" />}
        </Button>
      </div>

      {entries.length > 0
        ? <NodeMemoryEntryList entries={entries} />
        : isError
          ? <ErrorState error={error} />
          : <EmptyState node={node} memory={data} isExtractionWindowOpen={isExtractionWindowOpen} />}
    </div>
  )
}

function NodeMemoryEntryList({ entries }: { entries: AgentMemoryEntry[] }) {
  return (
    <div className="space-y-3" data-testid="node-memory-list">
      {entries.map(entry => (
        <div key={entry.id} className="space-y-2" data-testid="memory-entry">
          <div className="flex items-center gap-2">
            <Badge
              variant="outline"
              className={cn(
                'shrink-0 text-[10px] uppercase',
                entry.type === 'Fact'
                  ? 'text-info-foreground bg-info border-info-foreground'
                  : 'text-cancelled-foreground bg-cancelled border-cancelled-foreground',
              )}
            >
              {entry.type}
            </Badge>
            <Badge variant="outline" className="shrink-0 text-[10px] uppercase text-success-foreground bg-success border-success-foreground">
              New
            </Badge>
          </div>
          <p className="whitespace-pre-wrap break-words text-sm text-foreground">{entry.content}</p>
        </div>
      ))}
    </div>
  )
}

function Panel({ children, tone }: { children: React.ReactNode; tone?: 'danger' }) {
  const colour = tone === 'danger' ? 'text-danger-foreground' : 'text-muted-foreground'

  return (
    <div className={`rounded-2xl border border-dashed border-border bg-table-row p-8 text-center text-sm ${colour}`}>
      {children}
    </div>
  )
}

function ErrorState({ error }: { error: unknown }) {
  const forbidden = getErrorStatus(error) === 403

  return (
    <Panel tone="danger">
      <p role="alert">
        {forbidden
          ? "You don't have permission to view this node's memory."
          : getErrorMessage(error) ?? "Couldn't load what this node learned."}
      </p>
    </Panel>
  )
}

function EmptyState({
  node,
  memory,
  isExtractionWindowOpen,
}: {
  node: WorkflowRunNode
  memory: { isAgentNode: boolean; memoryEnabled: boolean } | null | undefined
  isExtractionWindowOpen: boolean
}) {
  if (!memory) {
    return <Panel>This node is no longer available.</Panel>
  }

  if (!memory.isAgentNode) {
    return <Panel>No agent version has been resolved for this node yet.</Panel>
  }

  if (!memory.memoryEnabled) {
    return (
      <Panel>
        Memory is not enabled for this agent. Add <code className="bg-muted px-1 py-0.5 rounded">memory: project</code> to
        its definition to let it learn from runs.
      </Panel>
    )
  }

  if (node.status === 'Skipped') {
    return <Panel>This node was skipped, so it learned nothing from this run.</Panel>
  }

  if (node.status === 'Failed') {
    return <Panel>This node didn't complete successfully, so it learned nothing from this run.</Panel>
  }

  if (!node.completedAt || node.status !== 'Completed') {
    return <Panel>This node hasn't finished yet. Anything it learns appears here once it completes.</Panel>
  }

  if (isExtractionWindowOpen) {
    return (
      <Panel>
        <span className="flex items-center justify-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-info-foreground" />
          Learning from this run…
        </span>
      </Panel>
    )
  }

  return (
    <Panel>
      Nothing new recorded for this run. Use refresh if the agent finished only moments ago.
    </Panel>
  )
}
